import React from "react"
import { Paper, TextField, FlatButton, Tabs, Tab } from "material-ui"
import strings from "../localization/strings"
import autobind from "class-autobind"
import AuthenticationStore from "../stores/AuthenticationStore"
import * as utils from "../helpers/utils"
import * as api from "../api"
import * as userActions from "../actions/UserActions"
import AppStateStore from "../stores/AppStateStore"
import AgreementSet from "../components/agreement/settting/AgreementSet"
import PasswordField from "../components/common/PasswordField"

interface IProps {

}
interface IState {
    accountName: string
    accountNameErrorText: string
    userFirstName: string
    userFirstNameErrorText: string
    userLastName: string
    userLastNameErrorText: string
    userEmail: string
    userEmailErrorText: string
    userPassword: string
    userPasswordErrorText: string
    userPasswordVerified: boolean
    userPasswordConfirm: string
    passwordConfirmationErrorText: string
    saveClicked: boolean
    loadingAgreement: boolean
    requireAgreement: boolean
}

export default class OemAdmin extends React.Component<IProps, IState> {
    constructor(props) {
        super(props)
        this.state = {
            accountName: "",
            accountNameErrorText: null,
            userFirstName: "",
            userFirstNameErrorText: null,
            userLastName: "",
            userLastNameErrorText: null,
            userEmail: "",
            userEmailErrorText: null,
            userPassword: "",
            userPasswordErrorText: null,
            userPasswordVerified: true,
            userPasswordConfirm: "",
            passwordConfirmationErrorText: null,
            saveClicked: false,
            loadingAgreement: true,
            requireAgreement: false,
        }
        autobind(this)
    }

    componentDidMount() {
        document.title = `OEM Admin | ${strings.Findd_Grandd_Central}`
        this.getFinddPolicyRequired()
    }

    handleAccountNameChange(event) {
        this.setState({ accountName: event.target.value }, () => this.state.saveClicked && this.validateForm())
    }

    handleUserEmailChange(event) {
        this.setState({ userEmail: event.target.value }, () => this.state.saveClicked && this.validateForm())
    }

    handlePasswordChange(password, passwordVerified) {
        this.setState({ userPassword: password, userPasswordVerified: passwordVerified }, () => this.state.saveClicked && this.validateForm())
    }

    handlePasswordConfirmChange(event) {
        this.setState({ userPasswordConfirm: event.target.value }, () => this.state.saveClicked && this.validateForm())
    }

    handleFirstNameChange(event) {
        this.setState({ userFirstName: event.target.value }, () => this.state.saveClicked && this.validateForm())
    }

    handleLastNameChange(event) {
        this.setState({ userLastName: event.target.value }, () => this.state.saveClicked && this.validateForm())
    }

    validateForm() {
        let returnVal = true

        if (this.state.accountName.length === 0) {
            this.setState({ accountNameErrorText: "Account name cannot be empty." })
            returnVal = false
        } else {
            this.setState({ accountNameErrorText: "" })
        }

        if (this.state.userFirstName.length === 0) {
            this.setState({ userFirstNameErrorText: strings.First_Name_cannot_be_empty })
            returnVal = false
        } else {
            this.setState({ userFirstNameErrorText: "" })
        }

        if (this.state.userLastName.length === 0) {
            this.setState({ userLastNameErrorText: "Last name cannot be empty." })
            returnVal = false
        } else {
            this.setState({ userLastNameErrorText: "" })
        }

        if (utils.validateEmail(this.state.userEmail)) {
            this.setState({ userEmailErrorText: "" })
        } else {
            this.setState({ userEmailErrorText: strings.Invalid_email_format })
            returnVal = false
        }

        //if we are editing, do not require a password. If we don't have a password set, then it won't update.
        if (!this.state.userPassword) {
            this.setState({ userPasswordErrorText: strings.Password_required })
            returnVal = false
        }
        else {
            if (!this.state.userPasswordVerified) {
                returnVal = false
            }
        }

        if (this.state.userPassword !== this.state.userPasswordConfirm) {
            this.setState({ passwordConfirmationErrorText: strings.Passwords_must_match })
            returnVal = false
        } else {
            this.setState({ passwordConfirmationErrorText: "" })
        }

        if (!returnVal) {
            return new Promise(fulfill => fulfill(false))
        }

        if (!this.state.userEmail) {
            this.setState({ userEmailErrorText: strings.Email_cannot_be_empty })
            return new Promise(fulfill => fulfill(false))
        }

        return userActions.emailExists(this.state.userEmail).then(result => {
            if (result.data) {
                this.setState({ userEmailErrorText: strings.Email_already_in_use })
                returnVal = false
            }
            return returnVal
        })
    }

    addNewAccount() {
        const userGuid = utils.guid()
        const newAccountId = utils.guid()
        const defaultUserRoleId = utils.guid()
        const account = {
            Id: newAccountId,
            Name: this.state.accountName,
            AccountSettings: {
                UserRoles: [
                    {
                        Id: defaultUserRoleId,
                        Type: "Account Owner",
                        Permissions: [
                            AuthenticationStore.Permissions.Account.AccountAdmin,
                            AuthenticationStore.Permissions.Account.EditHierarchy,
                            AuthenticationStore.Permissions.Account.ViewFullHierarchy,
                            AuthenticationStore.Permissions.Employees.AllowSelfEmpEdit,
                            AuthenticationStore.Permissions.Employees.AddEmployees,
                            AuthenticationStore.Permissions.Employees.DeleteEmployees,
                            AuthenticationStore.Permissions.Employees.EditEmployees,
                            AuthenticationStore.Permissions.Employees.ViewEmployees,
                            AuthenticationStore.Permissions.PunchRecords.EditExceptions,
                            AuthenticationStore.Permissions.PunchRecords.ViewRecords,
                            AuthenticationStore.Permissions.Devices.ViewAllDevices,
                            AuthenticationStore.Permissions.Devices.AddAllDevices,
                            AuthenticationStore.Permissions.Devices.EditAllDevices,
                            AuthenticationStore.Permissions.Devices.DeleteAllDevices,
                            AuthenticationStore.Permissions.Devices.InitDevices,
                            AuthenticationStore.Permissions.Devices.ViewHierarchyDevices,
                            AuthenticationStore.Permissions.Devices.AddHierarchyDevices,
                            AuthenticationStore.Permissions.Devices.EditHierarchyDevices,
                            AuthenticationStore.Permissions.Devices.DeleteHierarchyDevices,
                            AuthenticationStore.Permissions.Devices.ViewUserDevices,
                            AuthenticationStore.Permissions.Devices.AddUserDevices,
                            AuthenticationStore.Permissions.Devices.EditUserDevices,
                            AuthenticationStore.Permissions.Devices.DeleteUserDevices,
                            AuthenticationStore.Permissions.Timecards.ApproveTimecards,
                            AuthenticationStore.Permissions.Timecards.EditTimecards,
                            AuthenticationStore.Permissions.Timecards.ViewTimecards,
                            AuthenticationStore.Permissions.Payroll.ViewPayroll,
                            AuthenticationStore.Permissions.Payroll.LockPayroll,
                            AuthenticationStore.Permissions.Payroll.ExportPayroll
                        ]
                    }
                ]
            }
        }
        const newUser = {
            Id: userGuid,
            Email: this.state.userEmail,
            PasswordUpdate: this.state.userPassword,
            FirstName: this.state.userFirstName,
            LastName: this.state.userLastName,
            AccountUser: true,
            AccountId: newAccountId,
            UserRole: {
                Permissions: []
            },
            UserRoleId: defaultUserRoleId,
            IsEmployee: false,
        }

        api.addAccount(account).then(() =>
            api.addUser(newUser).then(() => {
                AppStateStore.setProgressSnackbarContent("The account has been created.")
                this.setState({
                    accountName: "",
                    userFirstName: "",
                    userLastName: "",
                    userEmail: "",
                    userPassword: "",
                    userPasswordConfirm: ""
                })
            })
        )
    }

    onAddNewAccountClick() {
        this.setState({ saveClicked: true })
        this.validateForm().then(result =>
            result && this.addNewAccount()
        )
    }

    getFinddPolicyRequired() {
        api.getLatestPrivacy().then(res =>
            this.setState({
                loadingAgreement: false,
                requireAgreement: res.data ? true : false
            })
        )
    }

    render() {
        return (
            <div className="page-container">
                <div className="page-container-parent">
                    <Paper zDepth={1} className="page-paper">
                        <div style={{ textAlign: "center" }}><h1>OEM Admin</h1></div>
                        <Tabs>
                            <Tab label="Create a new Account">
                                <div style={{ padding: 20 }}>
                                    <TextField
                                        autoFocus
                                        floatingLabelText="account name"
                                        value={this.state.accountName}
                                        errorText={this.state.accountNameErrorText}
                                        onChange={this.handleAccountNameChange}
                                        style={{ marginRight: 20, width: "100%" }}
                                    />
                                    <TextField
                                        floatingLabelText="first name"
                                        value={this.state.userFirstName}
                                        onChange={this.handleFirstNameChange}
                                        errorText={this.state.userFirstNameErrorText}
                                        style={{ marginRight: 20, width: "100%" }}
                                    />
                                    <TextField
                                        floatingLabelText="last name"
                                        value={this.state.userLastName}
                                        onChange={this.handleLastNameChange}
                                        errorText={this.state.userLastNameErrorText}
                                        style={{ marginRight: 20, width: "100%" }}
                                    />
                                    <TextField
                                        floatingLabelText={strings.user_email}
                                        value={this.state.userEmail}
                                        onChange={this.handleUserEmailChange}
                                        errorText={this.state.userEmailErrorText}
                                        style={{ marginRight: 20, width: "100%" }}
                                    />
                                    <PasswordField
                                        password={this.state.userPassword}
                                        errorPassword={this.state.userPasswordErrorText}
                                        handleChange={this.handlePasswordChange}
                                        handleSave={this.handlePasswordChange}
                                    />
                                    <TextField
                                        floatingLabelText={strings.user_password_confirm}
                                        value={this.state.userPasswordConfirm}
                                        onChange={this.handlePasswordConfirmChange}
                                        style={{ marginRight: 20, width: "100%" }}
                                        type="password"
                                    />
                                    <br />
                                    <br />
                                    <br />
                                    <FlatButton label="Add New Account" onClick={this.onAddNewAccountClick} />
                                </div>
                            </Tab>
                            {!AppStateStore.hideAgreementFeature &&
                                <Tab label="Create Findd Policy">
                                    <div style={{ height: 478, padding: 35 }}>
                                        {!this.state.loadingAgreement &&
                                            <AgreementSet
                                                finddAgreement
                                                cannotDisable
                                                requireAgreement={this.state.requireAgreement}
                                            />
                                        }
                                    </div>
                                </Tab>
                            }
                        </Tabs>
                    </Paper>
                </div>
            </div>
        )
    }
}
